import { formatUsageCount, formatUsd } from './format';
import { summarizeModelUsage } from './modelUsageSummary';
import { cacheHitPct } from './displayFormat';
import type { ModelUsage } from './types';

/** One model's row on the stats page: figures plus a max-normalized bar width (by tokens). */
interface UsageRow {
  exec: string;
  totalTokens: number;
  costUsd: number | null;
  pct: number;          // 0..100, largest row = 100
  tokensLabel: string;
  costLabel: string;    // '—' when no contributing request has a known price
  cacheHitPct: number | null; // cacheRead / (cacheRead + cacheWrite + input); null when nothing was read
}

export interface UsageSummary {
  rows: UsageRow[];
  totalCost: number;          // sum of known costs, zero when none are priced
  totalCostLabel: string;
  totalTokens: number;
  totalTokensLabel: string;
  totalCacheTokens: number;   // cacheRead + cacheWrite across all models
  totalCacheLabel: string;
  modelsUsed: number;
  hasAnyUsage: boolean;
}

/** Shape the raw `/usage/by-model` array into sorted, pre-formatted display rows + totals.
 *  Pure and unit-tested; the view stays declarative. Bar widths are max-normalized by tokens
 *  (the metric every executor reports), so cost-less models still get a meaningful bar. */
export function buildUsageSummary(data: ModelUsage[] | undefined, locale: string): UsageSummary {
  const summary = summarizeModelUsage(data ?? []);
  const { totalTokens, totalCacheTokens, totalCost, maxTokens } = summary;
  const rows: UsageRow[] = summary.models
    .map((m) => ({
      exec: m.exec,
      totalTokens: m.usage.total,
      costUsd: m.usage.costUsd,
      pct: (m.usage.total / maxTokens) * 100,
      tokensLabel: formatUsageCount(m.usage.total, locale),
      costLabel: formatUsd(m.usage.costUsd, locale, 4),
      cacheHitPct: cacheHitPct(m.usage),
    }));

  return {
    rows,
    totalCost,
    totalCostLabel: formatUsd(totalCost, locale, 4),
    totalTokens,
    totalTokensLabel: formatUsageCount(totalTokens, locale),
    totalCacheTokens,
    totalCacheLabel: formatUsageCount(totalCacheTokens, locale),
    modelsUsed: rows.length,
    // A provider may report a settled cost without token detail. Keep that row visible instead of
    // incorrectly replacing the ledger with the empty state.
    hasAnyUsage: summary.hasAnyUsage,
  };
}
